import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import facade from "../services/qwof/apiFacade";
import { useAuth } from "../context/AuthContext";
import Spinner from "../components/Spinner.jsx";
import CoinAmount from "../components/CoinAmount.jsx";
import NavigationRow from "../components/NavigationRow.jsx";
import DeleteConfirmationWindow from "../components/DeleteConfirmationWindow.jsx";
import QuagganEmoji from "../components/QuagganEmoji.jsx";
import ParticipantWindow from "../components/ParticipantWindow.jsx";
import QuagganPodium from "../components/QuagganPodium.jsx";
import WinnersTable from "../components/WinnersTable.jsx";
import ParticipantsGraph from "../components/ParticipantsGraph.jsx";
import LuckStatistics from "../components/LuckStatistics.jsx";
import GuildMembersTab from "../components/GuildMembersTab.jsx";
import NonparticipantsTab from "../components/NonparticipantsTab.jsx";
import { enrichItemsWithTradingPostPrices } from "../services/gw2API2/gw2API2";
import {
  calculateLuckStatistics,
  getLuckRankings,
  MIN_GAMES_FOR_LUCK_RANKING,
} from "../utils/giveawayLuck";

const rarityColors = {
  Basic: "#dce7ef",
  Fine: "#62d0ff",
  Masterwork: "#62d98d",
  Rare: "#ffd45b",
  Exotic: "#ffb14f",
  Ascended: "#ff7cf0",
  Legendary: "#bb7dff",
};

function getGameId(game) {
  const candidates = [game?.id, game?.gameId, game?.ID];
  const resolved = candidates.find((candidate) => candidate !== undefined && candidate !== null && candidate !== "");
  return resolved === undefined ? "" : String(resolved);
}

function getNormalizedName(value) {
  return (value || "").trim().toLowerCase();
}

function TabButton({ active, children, onClick }) {
  return (
    <button
      className={`names-btn ${active ? "primary" : "secondary"}`}
      onClick={onClick}
      style={{ minWidth: 110 }}
    >
      {children}
    </button>
  );
}

function PrizeCard({ result, index }) {
  const item = result.item;
  const accent = rarityColors[item?.rarity] || "#90e0ef";
  const quantity = Math.max(1, Number(result?.quantity) || 1);
  const buyUnit = Math.max(0, Number(item?.tp_buy_price) || 0);
  const sellUnit = Math.max(0, Number(item?.tp_sell_price) || 0);
  const buyTotal = buyUnit * quantity;
  const sellTotal = sellUnit * quantity;

  return (
    <div
      key={`${result.participantName}-${item?.id || "item"}-${index}`}
      style={{
        display: "grid",
        gridTemplateColumns: "56px minmax(0, 1fr)",
        gap: 12,
        alignItems: "center",
        padding: 10,
        borderRadius: 14,
        background: "rgba(8, 24, 50, 0.72)",
        border: `1px solid ${accent}55`,
        boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.03)",
      }}
    >
      <div
        style={{
          position: "relative",
          width: 56,
          height: 56,
          borderRadius: 12,
          border: `2px solid ${accent}`,
          background: "rgba(10, 16, 30, 0.92)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {item?.icon ? (
          <img
            src={item.icon}
            alt={item.name}
            style={{ width: 42, height: 42, objectFit: "contain" }}
          />
        ) : null}
        <span
          style={{
            position: "absolute",
            right: 4,
            bottom: 3,
            background: "rgba(0, 0, 0, 0.74)",
            color: "#fff",
            fontWeight: 700,
            fontSize: 12,
            lineHeight: 1,
            padding: "2px 5px",
            borderRadius: 6,
          }}
        >
          {quantity}
        </span>
      </div>

      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontWeight: 800,
            color: accent,
            fontSize: 14,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={item?.name || "Unknown item"}
        >
          {item?.name || "Unknown item"}
        </div>
        <div style={{ color: "#e7f7ff", fontSize: 13, marginTop: 4 }}>
          Won by <strong>{result.participantName}</strong>
        </div>
        <div style={{ color: "rgba(214,232,255,0.7)", fontSize: 12, marginTop: 2 }}>
          {item?.type || "Unknown type"}
          {item?.rarity ? ` · ${item.rarity}` : ""}
        </div>
        <div style={{ display: "grid", gap: 2, marginTop: 6 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12, color: "#cfeeff" }}>
            <span>Buy</span>
            <CoinAmount value={buyUnit} compact />
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12, color: "#cfeeff" }}>
            <span>Sell</span>
            <CoinAmount value={sellUnit} compact />
          </div>
          {quantity > 1 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12, color: "#cfeeff" }}>
              <span>Sell total</span>
              <CoinAmount value={sellTotal} compact />
            </div>
          )}
          {quantity > 1 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12, color: "#cfeeff" }}>
              <span>Buy total</span>
              <CoinAmount value={buyTotal} compact />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function HistoryTab({
  historyGames,
  historyLoading,
  historyError,
  onRefresh,
  canManageGames,
  onDeleteGameRequest,
  onUpdateGame,
}) {
  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          marginBottom: 14,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h3 style={{ margin: 0, color: "#40e8ff" }}>Giveaway History</h3>
          <p className="login-help-text" style={{ margin: "6px 0 0" }}>
            Review finished giveaway games and their winners.
          </p>
        </div>

        <button className="names-btn primary" onClick={onRefresh} disabled={historyLoading}>
          Refresh history
        </button>
      </div>

      {historyLoading ? (
        <>
          <Spinner />
          <p className="login-help-text" style={{ textAlign: "center", marginTop: -8 }}>
            Loading giveaway history
          </p>
        </>
      ) : null}

      {historyError ? (
        <>
          <pre
            className="register-error"
            style={{
              whiteSpace: "pre-wrap",
              marginTop: 8,
              background: "rgba(120, 20, 20, 0.5)",
              padding: 10,
              borderRadius: 10,
            }}
          >
            {historyError}
          </pre>
          <QuagganEmoji type="failure" />
        </>
      ) : null}

      {!historyLoading && !historyError && historyGames.length === 0 ? (
        <div
          style={{
            borderRadius: 16,
            border: "1px dashed rgba(144,224,239,0.35)",
            background: "rgba(8, 24, 50, 0.55)",
            padding: 22,
            textAlign: "center",
            color: "rgba(224,247,250,0.78)",
          }}
        >
          No giveaway history yet.
        </div>
      ) : null}

      {!historyLoading && !historyError && historyGames.length > 0 ? (
        <div
          className="admin-history-list"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            paddingBottom: 12,
          }}
        >
          {historyGames.map((game, index) => (
            <section
              key={`${getGameId(game) || game.title}-${game.createdByUsername}-${index}`}
              style={{
                borderRadius: 18,
                padding: 16,
                background: "linear-gradient(180deg, rgba(8, 24, 50, 0.92), rgba(5, 16, 34, 0.88))",
                border: "1px solid rgba(80, 180, 255, 0.24)",
                boxShadow: "0 10px 24px rgba(0, 0, 0, 0.22)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 12,
                  flexWrap: "wrap",
                  marginBottom: 14,
                }}
              >
                <div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: "#b9f1ff" }}>
                    {game.title}
                  </div>
                  <div style={{ marginTop: 6, color: "rgba(214,232,255,0.74)", fontSize: 13 }}>
                    Created by <strong>{game.createdByUsername}</strong>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                    justifyContent: "flex-end",
                  }}
                >
                  <span
                    style={{
                      padding: "6px 10px",
                      borderRadius: 999,
                      background: "rgba(15, 60, 110, 0.7)",
                      color: "#dff8ff",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {game.participants?.length || 0} participants
                  </span>
                  <span
                    style={{
                      padding: "6px 10px",
                      borderRadius: 999,
                      background: "rgba(90, 70, 10, 0.55)",
                      color: "#ffea9a",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {game.results?.length || 0} prizes
                  </span>
                  {canManageGames && getGameId(game) ? (
                    <button
                      className="names-btn secondary"
                      onClick={() => onUpdateGame(game)}
                    >
                      Edit
                    </button>
                  ) : null}
                  {canManageGames && getGameId(game) ? (
                    <button
                      className="names-btn"
                      onClick={() => onDeleteGameRequest(game)}
                      style={{
                        borderColor: "rgba(255, 120, 120, 0.72)",
                        background: "radial-gradient(circle at 20% 0%, rgba(170, 30, 60, 0.95), rgba(70, 5, 20, 0.95))",
                      }}
                    >
                      Delete
                    </button>
                  ) : null}
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <div style={{ fontWeight: 700, color: "#90e0ef", marginBottom: 8 }}>
                  Participants
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {(game.participants || []).map((participant) => (
                    <span
                      key={`${game.title}-${participant.id}-${participant.displayName}`}
                      style={{
                        padding: "7px 10px",
                        borderRadius: 999,
                        background: "rgba(10, 28, 55, 0.78)",
                        border: "1px solid rgba(144,224,239,0.16)",
                        color: "#eefbff",
                        fontSize: 13,
                      }}
                    >
                      {participant.displayName}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <div style={{ fontWeight: 700, color: "#90e0ef", marginBottom: 10 }}>
                  Results
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10 }}>
                  {(game.results || []).map((result, resultIndex) => (
                    <PrizeCard
                      key={`${game.title}-${result.participantName}-${result.item?.id || resultIndex}`}
                      result={result}
                      index={resultIndex}
                    />
                  ))}
                </div>
              </div>
            </section>
          ))}
        </div>
      ) : null}
    </>
  );
}

function SyncTab({
  guildId,
  setGuildId,
  busyAction,
  setBusyAction,
  result,
  setResult,
  error,
  setError,
}) {
  return (
    <>
      <h3 style={{ marginTop: 0 }}>Guild sync tools</h3>
      <p className="login-help-text">
        Enter a Guild ID and choose what to sync from the GW2 API.
      </p>

      <div style={{ marginTop: 8, marginBottom: 12 }}>
        <input
          type="text"
          className="login-input"
          placeholder="Guild ID (e.g. 0560C98B-...)"
          value={guildId}
          onChange={(e) => setGuildId(e.target.value)}
        />
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <button
          className="names-btn primary"
          disabled={!guildId || !!busyAction}
          onClick={async () => {
            if (!guildId) return;
            setBusyAction("guild");
            setResult("");
            setError("");
            try {
              const res = await facade.syncGuild(guildId.trim());
              setResult(typeof res === "string" ? res : JSON.stringify(res, null, 2));
            } catch (err) {
              const msg = err?.fullError || err?.message || "Unknown error";
              setError(`Sync guild failed: ${msg}`);
            } finally {
              setBusyAction(null);
            }
          }}
        >
          Sync guild
        </button>

        <button
          className="names-btn primary"
          disabled={!guildId || !!busyAction}
          onClick={async () => {
            if (!guildId) return;
            setBusyAction("members");
            setResult("");
            setError("");
            try {
              const res = await facade.syncGuildMembers(guildId.trim());
              setResult(typeof res === "string" ? res : JSON.stringify(res, null, 2));
            } catch (err) {
              const msg = err?.fullError || err?.message || "Unknown error";
              setError(`Sync members failed: ${msg}`);
            } finally {
              setBusyAction(null);
            }
          }}
        >
          Sync members
        </button>

        <button
          className="names-btn primary"
          disabled={!guildId || !!busyAction}
          onClick={async () => {
            if (!guildId) return;
            setBusyAction("stash");
            setResult("");
            setError("");
            try {
              const res = await facade.syncGuildStash(guildId.trim());
              setResult(typeof res === "string" ? res : JSON.stringify(res, null, 2));
            } catch (err) {
              const msg = err?.fullError || err?.message || "Unknown error";
              setError(`Sync stash failed: ${msg}`);
            } finally {
              setBusyAction(null);
            }
          }}
        >
          Sync stash
        </button>
      </div>

      {busyAction && (
        <>
          <Spinner />
          <p className="login-help-text" style={{ textAlign: "center", marginTop: -8 }}>
            {busyAction === "guild" && "Syncing guild..."}
            {busyAction === "members" && "Syncing members..."}
            {busyAction === "stash" && "Syncing stash..."}
          </p>
        </>
      )}

      {error && (
        <>
          <pre
            className="register-error"
            style={{
              whiteSpace: "pre-wrap",
              marginTop: 8,
              background: "rgba(120, 20, 20, 0.5)",
              padding: 8,
              borderRadius: 6,
            }}
          >
            {error}
          </pre>
          <QuagganEmoji type="failure" />
        </>
      )}

      {result && !error && (
        <>
          <pre
            className="login-help-text"
            style={{
              whiteSpace: "pre-wrap",
              marginTop: 8,
              background: "rgba(0, 40, 80, 0.6)",
              padding: 8,
              borderRadius: 6,
              maxHeight: 260,
              overflowY: "auto",
              fontSize: 13,
            }}
          >
            {result}
          </pre>
          <QuagganEmoji type="success" />
        </>
      )}
    </>
  );
}

function StatisticsTab({
  historyGames,
  historyLoading,
  historyError,
  onRefresh,
  nonparticipants,
  nonparticipantError,
  canManageGames,
  onMarkNonparticipants,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "wins", direction: "desc" });
  const [selectedParticipantKey, setSelectedParticipantKey] = useState("");
  const [selectedParticipantKeys, setSelectedParticipantKeys] = useState(new Set());
  const [markingError, setMarkingError] = useState("");
  const [markingMessage, setMarkingMessage] = useState("");
  const [markingProcessing, setMarkingProcessing] = useState(false);
  const [podiumSeedNonce, setPodiumSeedNonce] = useState(() => `${Date.now()}-${Math.random()}`);

  const nonparticipantNames = useMemo(
    () => new Set((nonparticipants || []).map((item) => getNormalizedName(item?.name)).filter(Boolean)),
    [nonparticipants],
  );

  const statsRows = useMemo(() => {
    const participantMap = new Map();

    const resolveParticipantKey = ({ displayName, participantId }) => {
      const normalizedName = getNormalizedName(displayName);
      if (normalizedName) return `name:${normalizedName}`;
      if (participantId) return `id:${participantId}`;
      return "name:unknown participant";
    };

    const ensureParticipant = (participantKey, displayName) => {
      if (!participantMap.has(participantKey)) {
        participantMap.set(participantKey, {
          key: participantKey,
          name: displayName,
          gamesParticipated: 0,
          wins: 0,
          totalValue: 0,
          gamesSet: new Set(),
          prizes: [],
        });
      }
      return participantMap.get(participantKey);
    };

    (historyGames || []).forEach((game) => {
      const participants = Array.isArray(game?.participants) ? game.participants : [];
      const results = Array.isArray(game?.results) ? game.results : [];
      const gameSeen = new Set();
      const nameToKey = new Map();

      participants.forEach((participant) => {
        const displayName = (participant?.displayName || participant?.name || "").trim();
        if (nonparticipantNames.has(getNormalizedName(displayName))) return;
        const participantKey = resolveParticipantKey({
          displayName,
          participantId: participant?.id,
        });
        const entry = ensureParticipant(participantKey, displayName || "Unknown participant");
        const gameTitle = game?.title || `Game ${getGameId(game) || "unknown"}`;
        const normalizedName = getNormalizedName(displayName);

        if (!gameSeen.has(participantKey)) {
          entry.gamesParticipated += 1;
          gameSeen.add(participantKey);
        }
        entry.gamesSet.add(gameTitle);

        if (normalizedName && !nameToKey.has(normalizedName)) {
          nameToKey.set(normalizedName, participantKey);
        }
      });

      results.forEach((result) => {
        const winnerName = (result?.participantName || "").trim();
        if (!winnerName) return;
        if (nonparticipantNames.has(getNormalizedName(winnerName))) return;

        const normalizedWinnerName = getNormalizedName(winnerName);
        const participantKey =
          nameToKey.get(normalizedWinnerName) ||
          resolveParticipantKey({ displayName: winnerName });
        const entry = ensureParticipant(participantKey, winnerName);
        const gameTitle = game?.title || `Game ${getGameId(game) || "unknown"}`;

        if (!nameToKey.has(normalizedWinnerName)) {
          nameToKey.set(normalizedWinnerName, participantKey);
        }

        entry.wins += 1;
        const quantity = Math.max(1, Number(result?.quantity) || 1);
        const sellPrice = Math.max(0, Number(result?.item?.tp_sell_price) || 0);
        const buyPrice = Math.max(0, Number(result?.item?.tp_buy_price) || 0);
        const totalSellValue = sellPrice * quantity;
        const totalBuyValue = buyPrice * quantity;
        entry.totalValue += totalSellValue;
        entry.prizes.push({
          itemId: result?.item?.id,
          itemName: result?.item?.name || "Unknown item",
          icon: result?.item?.icon || "",
          quantity,
          tpSellPrice: sellPrice,
          tpBuyPrice: buyPrice,
          totalSellValue,
          totalBuyValue,
          gameTitle,
        });
        entry.gamesSet.add(gameTitle);

        if (!gameSeen.has(participantKey)) {
          entry.gamesParticipated += 1;
          gameSeen.add(participantKey);
        }
      });
    });

    const totalGames = (historyGames || []).length;
    const luckByName = new Map(
      calculateLuckStatistics(historyGames, nonparticipantNames)
        .map((participant) => [getNormalizedName(participant.name), participant.luckScore]),
    );

    const ranked = Array.from(participantMap.values())
      .map((row) => {
        const attendance = totalGames > 0 ? (row.gamesParticipated / totalGames) * 100 : 0;
        const winRate = row.gamesParticipated > 0 ? (row.wins / row.gamesParticipated) * 100 : 0;
        const luckScore = luckByName.get(getNormalizedName(row.name)) || 0;
        return {
          ...row,
          attendance,
          winRate,
          luckScore,
          games: Array.from(row.gamesSet),
        };
      })
      .sort((a, b) => {
        if (b.wins !== a.wins) return b.wins - a.wins;
        if (b.totalValue !== a.totalValue) return b.totalValue - a.totalValue;
        if (b.gamesParticipated !== a.gamesParticipated) return b.gamesParticipated - a.gamesParticipated;
        return a.name.localeCompare(b.name);
      })
      .map((row, index) => ({ ...row, rank: index + 1 }));

    return {
      totalGames,
      ranked,
      podium: ranked.slice(0, 3),
    };
  }, [historyGames, nonparticipantNames]);

  const luckRankings = useMemo(() => {
    const luckParticipants = calculateLuckStatistics(historyGames, nonparticipantNames);
    return getLuckRankings(luckParticipants, MIN_GAMES_FOR_LUCK_RANKING);
  }, [historyGames, nonparticipantNames]);

  const filteredGames = useMemo(
    () => (historyGames || []).map((game) => ({
      ...game,
      participants: (game.participants || []).filter(
        (participant) => !nonparticipantNames.has(getNormalizedName(participant?.displayName || participant?.name)),
      ),
      results: (game.results || []).filter(
        (result) => !nonparticipantNames.has(getNormalizedName(result?.participantName)),
      ),
    })),
    [historyGames, nonparticipantNames],
  );

  const visibleRows = useMemo(() => {
    const filtered = statsRows.ranked.filter((row) =>
      row.name.toLowerCase().includes(searchTerm.trim().toLowerCase())
    );

    const sorted = [...filtered].sort((a, b) => {
      const { key, direction } = sortConfig;
      const multiplier = direction === "asc" ? 1 : -1;

      if (key === "name") {
        return a.name.localeCompare(b.name) * multiplier;
      }

      return ((a[key] || 0) - (b[key] || 0)) * multiplier;
    });

    return sorted;
  }, [searchTerm, sortConfig, statsRows.ranked]);

  const selectedParticipant = useMemo(
    () => statsRows.ranked.find((row) => row.key === selectedParticipantKey) || null,
    [selectedParticipantKey, statsRows.ranked],
  );

  const podiumRows = useMemo(() => {
    const first = statsRows.podium.find((row) => row.rank === 1);
    const second = statsRows.podium.find((row) => row.rank === 2);
    const third = statsRows.podium.find((row) => row.rank === 3);
    return [second, first, third].filter(Boolean);
  }, [statsRows.podium]);

  const handleRefreshStatistics = async () => {
    setPodiumSeedNonce(`${Date.now()}-${Math.random()}`);
    await onRefresh();
  };

  const handleToggleParticipant = (key) => {
    setSelectedParticipantKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleMarkNonparticipants = async () => {
    const names = [...selectedParticipantKeys]
      .map((key) => statsRows.ranked.find((row) => row.key === key)?.name)
      .filter(Boolean)
      .map((name) => name.trim())
      .filter((name, index, values) =>
        values.findIndex((value) => getNormalizedName(value) === getNormalizedName(name)) === index
      );
    if (names.length === 0) return;
    if (!window.confirm(`Mark ${names.length} participant${names.length === 1 ? "" : "s"} as nonparticipant${names.length === 1 ? "" : "s"}? This changes statistics for everyone.`)) return;

    setMarkingProcessing(true);
    setMarkingError("");
    setMarkingMessage("");
    try {
      const created = await onMarkNonparticipants(names);
      const createdCount = Array.isArray(created) ? created.length : 0;
      setMarkingMessage(`Marked ${createdCount} as nonparticipants (${names.length - createdCount} already existed)`);
      setSelectedParticipantKeys(new Set());
    } catch (err) {
      const msg = err?.fullError || err?.message || "Unknown error";
      setMarkingError(`Failed to mark nonparticipants: ${msg}`);
    } finally {
      setMarkingProcessing(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig((current) => {
      if (current.key === key) {
        return {
          key,
          direction: current.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: key === "name" ? "asc" : "desc" };
    });
  };

  const formatPercent = (value) => `${value.toFixed(1)}%`;

  const renderSortLabel = (label, key) => {
    const isActive = sortConfig.key === key;
    const arrow = isActive ? (sortConfig.direction === "asc" ? " ↑" : " ↓") : "";
    return `${label}${arrow}`;
  };

  return (
    <div className="admin-stats-tab">
      <div
        className="admin-stats-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          marginBottom: 14,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h3 style={{ margin: 0, color: "#40e8ff" }}>Participant Statistics</h3>
          <p className="login-help-text" style={{ margin: "6px 0 0" }}>
            Stats across {statsRows.totalGames} total giveaway games.
          </p>
        </div>

        <button className="names-btn primary" onClick={handleRefreshStatistics} disabled={historyLoading}>
          Refresh statistics
        </button>
      </div>

      {historyLoading ? (
        <>
          <Spinner />
          <p className="login-help-text" style={{ textAlign: "center", marginTop: -8 }}>
            Loading participant statistics
          </p>
        </>
      ) : null}

      {historyError ? (
        <>
          <pre
            className="register-error"
            style={{
              whiteSpace: "pre-wrap",
              marginTop: 8,
              background: "rgba(120, 20, 20, 0.5)",
              padding: 10,
              borderRadius: 10,
            }}
          >
            {historyError}
          </pre>
          <QuagganEmoji type="failure" />
        </>
      ) : null}

      {nonparticipantError ? (
        <pre className="register-error" style={{ whiteSpace: "pre-wrap", marginTop: 8 }}>
          {nonparticipantError}
        </pre>
      ) : null}
      {markingError ? (
        <pre className="register-error" style={{ whiteSpace: "pre-wrap", marginTop: 8 }}>
          {markingError}
        </pre>
      ) : null}
      {markingMessage ? <p className="login-help-text" style={{ color: "#9ef0b0" }}>{markingMessage}</p> : null}

      {!historyLoading && !historyError && statsRows.ranked.length === 0 ? (
        <div
          style={{
            borderRadius: 16,
            border: "1px dashed rgba(144,224,239,0.35)",
            background: "rgba(8, 24, 50, 0.55)",
            padding: 22,
            textAlign: "center",
            color: "rgba(224,247,250,0.78)",
          }}
        >
          No participant data available yet.
        </div>
      ) : null}

      {!historyLoading && !historyError && statsRows.ranked.length > 0 ? (
        <>
          <QuagganPodium podiumRows={podiumRows} podiumSeedNonce={podiumSeedNonce} />

          <div className="admin-stats-search" style={{ marginBottom: 10 }}>
            <input
              type="text"
              className="login-input"
              placeholder="Search participants by name"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>

          <WinnersTable
            className="admin-stats-table"
            visibleRows={visibleRows}
            onSort={handleSort}
            renderSortLabel={renderSortLabel}
            onSelectParticipant={setSelectedParticipantKey}
            formatPercent={formatPercent}
            selectable={canManageGames}
            selectedParticipantKeys={selectedParticipantKeys}
            onToggleParticipant={handleToggleParticipant}
          />

          {canManageGames ? (
            <div style={{ margin: "12px 0" }}>
              <button
                className="names-btn primary"
                onClick={handleMarkNonparticipants}
                disabled={markingProcessing || selectedParticipantKeys.size === 0}
              >
                {markingProcessing ? "Marking..." : "Mark as nonparticipant(s)"}
              </button>
            </div>
          ) : null}

          <LuckStatistics
            luckiest={luckRankings.luckiest}
            unluckiest={luckRankings.unluckiest}
            minimumGames={MIN_GAMES_FOR_LUCK_RANKING}
          />

          <ParticipantsGraph historyGames={filteredGames} />

          <ParticipantWindow
            visible={!!selectedParticipant}
            participant={selectedParticipant}
            onClose={() => setSelectedParticipantKey("")}
          />
        </>
      ) : null}
    </div>
  );
}

function AdminPage({
  readOnly = false,
  allowSync = true,
  basePath = "/admin",
  requiredRoles = ["ADMIN", "LEADER", "OFFICER"],
  pageTitle = "Admin Page",
  pageDescription,
}) {
  const { isLoggedIn, username } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [hasAccess, setHasAccess] = useState(false);
  const [guildId, setGuildId] = useState("DA835590-BFBA-4D4C-BF10-014905F32E00");
  const [busyAction, setBusyAction] = useState(null); // "guild" | "members" | "stash" | null
  const [result, setResult] = useState("");
  const [error, setError] = useState("");
  const [historyGames, setHistoryGames] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [nonparticipants, setNonparticipants] = useState([]);
  const [nonparticipantsLoading, setNonparticipantsLoading] = useState(false);
  const [nonparticipantError, setNonparticipantError] = useState("");
  const [deleteTargetGame, setDeleteTargetGame] = useState(null);
  const [deleteSucceeded, setDeleteSucceeded] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [deleteProcessing, setDeleteProcessing] = useState(false);
  const normalizedBasePath = basePath.replace(/\/+$/, "");
  const allowManagement = !readOnly;
  const canManageGames = allowManagement && facade.hasAnyRole(["ADMIN", "LEADER"]);
  const allowedTabs = useMemo(
    () => {
      const tabs = allowSync ? ["history", "statistics", "sync"] : ["history", "statistics"];
      if (!readOnly || isLoggedIn) {
        tabs.splice(2, 0, "members");
      }
      return canManageGames ? [...tabs, "nonparticipants"] : tabs;
    },
    [allowSync, canManageGames, isLoggedIn, readOnly],
  );
  const activeTab = allowedTabs.find((tab) => location.pathname.endsWith(`/${tab}`)) || "history";
  const resolvedDescription =
    pageDescription ||
    (readOnly
      ? `Welcome ${username}, this is a read-only giveaway overview.`
      : `Welcome ${username}, you have admin access.`);

  useEffect(() => {
    const { documentElement, body } = document;
    const previousHtmlOverflowX = documentElement.style.overflowX;
    const previousHtmlOverflowY = documentElement.style.overflowY;
    const previousHtmlHeight = documentElement.style.height;
    const previousBodyOverflowX = body.style.overflowX;
    const previousBodyOverflowY = body.style.overflowY;
    const previousBodyHeight = body.style.height;

    documentElement.style.overflowX = "hidden";
    documentElement.style.overflowY = "auto";
    documentElement.style.height = "auto";
    body.style.overflowX = "hidden";
    body.style.overflowY = "auto";
    body.style.height = "auto";

    return () => {
      documentElement.style.overflowX = previousHtmlOverflowX;
      documentElement.style.overflowY = previousHtmlOverflowY;
      documentElement.style.height = previousHtmlHeight;
      body.style.overflowX = previousBodyOverflowX;
      body.style.overflowY = previousBodyOverflowY;
      body.style.height = previousBodyHeight;
    };
  }, []);

  useEffect(() => {
    // Public pages (requiredRoles.length === 0) don't need authentication
    const isPublicPage = requiredRoles.length === 0;
    const hasRequiredRole = isLoggedIn && facade.hasAnyRole(requiredRoles);
    const allowed = isPublicPage || hasRequiredRole;

    if (allowed) {
      setHasAccess(true);
    } else {
      navigate("/");
    }
  }, [isLoggedIn, navigate, requiredRoles]);

  useEffect(() => {
    if (!hasAccess) return;

    const normalizedPath = location.pathname.replace(/\/+$/, "");

    if (normalizedPath === normalizedBasePath) {
      navigate(`${normalizedBasePath}/history`, { replace: true });
      return;
    }

    const validPaths = allowedTabs.map((tab) => `${normalizedBasePath}/${tab}`);

    if (!validPaths.includes(normalizedPath)) {
      navigate(`${normalizedBasePath}/history`, { replace: true });
    }
  }, [allowedTabs, hasAccess, location.pathname, navigate, normalizedBasePath]);

  const loadHistory = async () => {
    setHistoryLoading(true);
    setHistoryError("");
    try {
      const games = await facade.getGames();
      const normalizedGames = Array.isArray(games) ? games : [];

      const uniqueItems = [];
      const seenItemIds = new Set();

      normalizedGames.forEach((game) => {
        const results = Array.isArray(game?.results) ? game.results : [];
        results.forEach((result) => {
          const item = result?.item;
          const id = Number(item?.id);
          if (!Number.isFinite(id) || id <= 0 || seenItemIds.has(id)) return;
          seenItemIds.add(id);
          uniqueItems.push(item);
        });
      });

      const pricedItems = await enrichItemsWithTradingPostPrices(uniqueItems);
      const pricedById = new Map(
        pricedItems.map((item) => [String(item?.id), item]),
      );

      const enrichedGames = normalizedGames.map((game) => {
        const results = Array.isArray(game?.results) ? game.results : [];

        return {
          ...game,
          results: results.map((result) => {
            const item = result?.item;
            const pricedItem = pricedById.get(String(item?.id));

            if (!pricedItem) return result;

            return {
              ...result,
              item: {
                ...item,
                ...pricedItem,
              },
            };
          }),
        };
      });

      setHistoryGames(enrichedGames);
    } catch (err) {
      const msg = err?.fullError || err?.message || "Unknown error";
      setHistoryError(`Failed to load giveaway history: ${msg}`);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadNonparticipants = async () => {
    setNonparticipantsLoading(true);
    setNonparticipantError("");
    try {
      const data = await facade.getNonparticipants();
      setNonparticipants(Array.isArray(data) ? data : []);
    } catch (err) {
      const msg = err?.fullError || err?.message || "Unknown error";
      setNonparticipantError(`Failed to load nonparticipants: ${msg}`);
      setNonparticipants([]);
    } finally {
      setNonparticipantsLoading(false);
    }
  };

  useEffect(() => {
    if (!hasAccess) return;
    if (activeTab === "history" || activeTab === "statistics") {
      loadHistory();
    }
    if (activeTab === "statistics" || activeTab === "nonparticipants") {
      loadNonparticipants();
    }
  }, [hasAccess, activeTab]);

  const handleMarkNonparticipants = async (names) => {
    const created = await facade.createNonparticipants(names.map((name) => ({ name })));
    await loadNonparticipants();
    return created;
  };

  const handleUpdateGame = (game) => {
    const gameId = getGameId(game);
    if (!gameId) return;
    navigate(`/game/${gameId}`, { state: { game } });
  };

  const handleDeleteGame = async () => {
    const gameId = getGameId(deleteTargetGame);
    if (!gameId) return;

    setDeleteProcessing(true);
    setDeleteError("");

    try {
      await facade.deleteGame(gameId);
      await loadHistory();
      setDeleteSucceeded(true);
    } catch (err) {
      const message = err?.fullError || err?.message || "Unknown error";
      setDeleteError(`Failed to delete game: ${message}`);
    } finally {
      setDeleteProcessing(false);
    }
  };

  if (!hasAccess) {
    return null;
  }

  return (
    <div className="admin-route">
      <NavigationRow className="admin-nav-row" />
      <div className="admin-route-inner">
        <div className="admin-panel">
          <h2 className="register-title">{pageTitle}</h2>
          <p className="login-help-text">
            {resolvedDescription}
          </p>

          <hr style={{ margin: "16px 0", borderColor: "rgba(144,224,239,0.3)" }} />

          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
            <TabButton
              active={activeTab === "history"}
              onClick={() => navigate(`${normalizedBasePath}/history`)}
            >
              History
            </TabButton>
            {canManageGames ? (
              <TabButton
                active={activeTab === "nonparticipants"}
                onClick={() => navigate(`${normalizedBasePath}/nonparticipants`)}
              >
                Nonparticipants
              </TabButton>
            ) : null}
            <TabButton
              active={activeTab === "statistics"}
              onClick={() => navigate(`${normalizedBasePath}/statistics`)}
            >
              Statistics
            </TabButton>
            {allowedTabs.includes("members") ? (
              <TabButton
                active={activeTab === "members"}
                onClick={() => navigate(`${normalizedBasePath}/members`)}
              >
                Members
              </TabButton>
            ) : null}
            {allowSync ? (
              <TabButton active={activeTab === "sync"} onClick={() => navigate(`${normalizedBasePath}/sync`)}>
                Sync
              </TabButton>
            ) : null}
          </div>

          {activeTab === "history" ? (
            <HistoryTab
              historyGames={historyGames}
              historyLoading={historyLoading}
              historyError={historyError}
              onRefresh={loadHistory}
              canManageGames={canManageGames}
              onDeleteGameRequest={(game) => {
                setDeleteError("");
                setDeleteTargetGame(game);
              }}
              onUpdateGame={handleUpdateGame}
            />
          ) : activeTab === "statistics" ? (
            <StatisticsTab
              historyGames={historyGames}
              historyLoading={historyLoading}
              historyError={historyError}
              onRefresh={loadHistory}
              nonparticipants={nonparticipants}
              nonparticipantError={nonparticipantError}
              canManageGames={canManageGames}
              onMarkNonparticipants={handleMarkNonparticipants}
            />
          ) : activeTab === "nonparticipants" ? (
            <NonparticipantsTab
              nonparticipants={nonparticipants}
              loading={nonparticipantsLoading}
              error={nonparticipantError}
              onRefresh={loadNonparticipants}
              onChanged={loadNonparticipants}
            />
          ) : activeTab === "members" ? (
            <GuildMembersTab guildId={guildId} />
          ) : (
            allowSync ? (
              <SyncTab
                guildId={guildId}
                setGuildId={setGuildId}
                busyAction={busyAction}
                setBusyAction={setBusyAction}
                result={result}
                setResult={setResult}
                error={error}
                setError={setError}
              />
            ) : null
          )}
        </div>
      </div>

      <DeleteConfirmationWindow
        visible={!!deleteTargetGame}
        title={deleteTargetGame?.title || ""}
        processing={deleteProcessing}
        error={deleteError}
        succeeded={deleteSucceeded}
        onCancel={() => {
          if (deleteProcessing) return;
          setDeleteError("");
          setDeleteTargetGame(null);
        }}
        onConfirm={handleDeleteGame}
        onSuccessClose={() => {
          setDeleteSucceeded(false);
          setDeleteTargetGame(null);
          setDeleteError("");
        }}
      />
    </div>
  );
}

export default AdminPage;
